import {
  Stack,
  IconButton,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import { LTCPContext } from "LTCP Module/LTCPContext";
import { useContext, useEffect, useState } from "react";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useNavigate } from "react-router-dom";
import ltcpRoutes from "LTCP Module/constants/ltcpRoutes";
import {
  addNewRole,
  deleteUserRole,
  getRoleManagementData,
  updateUserRole,
} from "LTCP Module/api/ltcpBackend";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMsal } from "@azure/msal-react";
import {
  addUserInAdGroup,
  getUserDetails,
  removeUserFromAdGroup,
} from "LTCP Module/api/graphApi";


// This is a stateless card to show the authorize user with their
// role and access details. On clicking any action button in this card
// it will call the specific callback function with the row details.
function UserRow({
  email,
  write,
  all,
  bg,
  category,
  mdm,
  role,
  onDelete,
  onEdit,
  userEmail,
}) {
  let specific_role = "";

  if (write) {
    if (mdm) {
      specific_role = "MDM edit access";
    } else if (role) {
      specific_role = "Role manager";
    } else if (all) {
      specific_role = "HUL access";
    } else if (bg) {
      specific_role = "BG: " + bg;
    } else {
      specific_role = "Category: " + category;
    }
  } else {
    if (all) {
      specific_role = "HUL access";
    } else if (bg) {
      specific_role = "BG: " + bg;
    } else {
      specific_role = "Category: " + category;
    }
  }

  return (
    <Paper sx={{ borderRadius: 4 }}>
      <Stack direction="row" px={2} py={1}>
        <Typography
          fontWeight={600}
          fontSize={14}
          sx={{ width: "40%", minWidth: "40%", maxWidth: "40%" }}
        >
          {email}
        </Typography>
        <Typography
          color={write ? "orangered" : "dodgerblue"}
          fontWeight="bold"
          sx={{ width: "15%", minWidth: "15%", maxWidth: "15%" }}
        >
          {write ? "Read-Write" : "Read-Only"}
        </Typography>
        <Typography
          color="#888"
          sx={{ width: "30%", minWidth: "30%", maxWidth: "30%" }}
        >
          {specific_role}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="end"
          gap={1}
          sx={{ width: "15%", minWidth: "15%", maxWidth: "15%" }}
        >
          <IconButton
            disabled={userEmail.toLowerCase() === email.toLowerCase()}
            size="small"
            onClick={onEdit}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            disabled={userEmail.toLowerCase() === email.toLowerCase()}
            size="small"
            onClick={onDelete}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
}


// This is the user role management page component. After loading this page
// it will fetch the currently available user data with their role and access
// and will show the data using the user row component. This also contain
// new user add logic, existing user role update logic and existing user
// remove logic.
function RoleManagement() {
  const navigate = useNavigate();

  const { accounts, instance } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;
  const { setLoading, setAlertWidget } = useContext(LTCPContext);

  const request = {
    scopes: ["user.read"],
    account: activeAccount,
  };

  const [refetch, setRefetch] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [bgOptions, setBgOptions] = useState([]);
  const [result, setResult] = useState([]);
  const [addRole, setAddRole] = useState(false);
  const [emailState, setEmailState] = useState("");
  const [canWriteState, setCanWriteState] = useState(false);
  const [accessLevel, setAccessLevel] = useState("hul");
  const [specificBg, setSpecificBg] = useState("");
  const [specificCategory, setSpecificCategory] = useState("");
  const [deleteRole, setDeleteRole] = useState(null);
  const [editRole, setEditRole] = useState(null);

  function refetchNow() {
    setRefetch(!refetch);
  }

  const getAccessToken = async () => {
    try {
      const response = await instance.acquireTokenSilent(request);
      console.log("Access Token1:", response.accessToken);
      return response.accessToken;
    } catch (error) {
      try {
        await instance.initialize();

        const response = await instance.acquireTokenSilent(request);
        console.log("Access Token2:", response.accessToken);
        return response.accessToken;
      } catch (error) {
        console.error("Error acquiring token silently:", error);

        try {
          const response = await instance.acquireTokenRedirect(request);
          console.log("Access Token3:", response.accessToken);
          return response.accessToken;
        } catch (error) {
          console.error("Error acquiring token interactively:", error);
        }
      }
    }
  };

  async function fetchData() {
    setLoading(true);

    const data = await getRoleManagementData();

    setResult(data.roles);
    setBgOptions(data.bgs || []);
    setCategoryOptions(data.categories || []);

    if (data.bgs && data.bgs.length > 0) {
      setSpecificBg(data.bgs[0]);
    }

    if (data.categories && data.categories.length > 0) {
      setSpecificCategory(data.categories[0]);
    }

    setLoading(false);
  }

  async function addUserApi() {
    try {
      if (emailState === "") {
        return alert("Please enter the user email!");
      }

      setLoading(true);

      const accessToken = await getAccessToken();
      const userId = await getUserDetails(accessToken, emailState);

      if (userId === null) {
        setLoading(false);
        setAlertWidget({
          open: true,
          success: false,
          message:
            "User not found. Please check the email address and try again.",
        });
        return;
      }

      const status = await addUserInAdGroup(accessToken, userId);
      if (status === 400) {
        setLoading(false);
        setAlertWidget({
          open: true,
          success: false,
          message: "User already exist in the group!",
        });

        return;
      } else if (status > 299) {
        setLoading(false);
        setAlertWidget({
          open: true,
          success: false,
        });

        return;
      }

      await addNewRole({
        email: emailState,
        write: canWriteState,
        bg: specificBg,
        category: specificCategory,
        level: accessLevel,
      });

      setLoading(false);
      setAddRole(false);
      setEmailState("");
      setAccessLevel("hul");
      setCanWriteState(false);
      setAlertWidget({
        open: true,
        success: true,
        message: "New user added successfully!",
        onClose: refetchNow,
      });
    } catch (e) {
      console.error(e);
      setLoading(false);
      setAlertWidget({
        open: true,
        success: false,
      });
    }
  }

  async function updateRoleApi() {
    try {
      setLoading(true);

      await updateUserRole({
        email: editRole,
        write: canWriteState,
        bg: specificBg,
        category: specificCategory,
        level: accessLevel,
      });

      setLoading(false);
      setEditRole(null);
      setEmailState("");
      setAccessLevel("hul");
      setCanWriteState(false);
      setAlertWidget({
        open: true,
        success: true,
        message: "User access updated successfully!",
        onClose: refetchNow,
      });
    } catch (e) {
      console.error(e);
      setLoading(false);
      setAlertWidget({
        open: true,
        success: false,
      });
    }
  }

  async function deleteRoleApi() {
    try {
      setLoading(true);

      const accessToken = await getAccessToken();
      const userId = await getUserDetails(accessToken, deleteRole);

      if (userId === null) {
        setLoading(false);
        setAlertWidget({
          open: true,
          success: false,
          message:
            "User not found. Please check the email address and try again.",
        });
        setDeleteRole(null);
        return;
      }

      await Promise.all([
        removeUserFromAdGroup(accessToken, userId),
        deleteUserRole(deleteRole),
      ]);

      setLoading(false);
      setDeleteRole(null);
      setAlertWidget({
        open: true,
        success: true,
        message: "User removed successfully!",
        onClose: refetchNow,
      });
    } catch (e) {
      console.error(e);
      setLoading(false);
      setAlertWidget({ open: true, success: false });
    }
  }

  useEffect(() => {
    fetchData();
  }, [refetch]);

  return (
    <Stack p={4} gap={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" justifyContent="start" alignItems="center">
          <IconButton
            sx={{ color: "black" }}
            onClick={() => navigate(ltcpRoutes.LANDING)}
          >
            <KeyboardBackspaceIcon />
          </IconButton>
          <Typography fontWeight="bold" fontSize={21}>
            Role Management
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="contained"
          onClick={() => setAddRole(true)}
        >
          Add New User
        </Button>
      </Stack>
      <Stack gap={1}>
        {result.map((item, index) => (
          <UserRow
            key={index}
            email={item.email}
            write={item.write}
            all={item.all}
            bg={item.bg}
            category={item.category}
            mdm={item.mdm}
            role={item.role}
            onEdit={() => {
              setEditRole(item.email);
              setCanWriteState(item.write);

              let level = "hul";

              if (item.bg) {
                level = "bg";
                setSpecificBg(item.bg);
              } else if (item.category) {
                level = "category";
                setSpecificCategory(item.category);
              } else if (item.mdm) {
                level = "mdm";
              } else if (item.role) {
                level = "role";
              }

              setAccessLevel(level);
            }}
            onDelete={() => setDeleteRole(item.email)}
            userEmail={userEmail}
          />
        ))}
      </Stack>
      <Dialog
        open={addRole || Boolean(editRole)}
        onClose={() => {
          setAddRole(false);
          setEditRole(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {addRole && "Add New User"} {editRole && "Edit User Access"}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} py={1}>
            {addRole && (
              <TextField
                label="Email"
                size="small"
                value={emailState}
                onChange={(e) => setEmailState(e.target.value)}
              />
            )}
            {editRole && (
              <TextField label="Email" size="small" value={editRole} disabled />
            )}
            <Stack direction="row" gap={1}>
              <Button
                size="small"
                variant="contained"
                sx={{ width: "100%" }}
                color={!canWriteState ? "primary" : "transparent"}
                onClick={() => {
                  if (accessLevel === "mdm" || accessLevel === "role") {
                    setAccessLevel("hul");
                  }
                  setCanWriteState(false);
                }}
              >
                Read-Only
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{ width: "100%" }}
                color={canWriteState ? "primary" : "transparent"}
                onClick={() => setCanWriteState(true)}
              >
                Read-Write
              </Button>
            </Stack>
            <Stack direction="row" alignItems="center">
              <IconButton size="small" onClick={() => setAccessLevel("hul")}>
                {accessLevel === "hul" ? (
                  <RadioButtonCheckedIcon fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" />
                )}
              </IconButton>
              <Typography fontSize={14}>Entire HUL Access</Typography>
            </Stack>
            <Stack
              justifyContent="space-between"
              direction="row"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center">
                <IconButton size="small" onClick={() => setAccessLevel("bg")}>
                  {accessLevel === "bg" ? (
                    <RadioButtonCheckedIcon fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" />
                  )}
                </IconButton>
                <Typography fontSize={14}>BG Specific Access</Typography>
              </Stack>
              <Select
                sx={{ width: 200 }}
                size="small"
                value={specificBg}
                disabled={accessLevel !== "bg"}
                onChange={(e) => setSpecificBg(e.target.value)}
              >
                {bgOptions.map((item, index) => (
                  <MenuItem key={index} value={item}>
                    {item.replaceAll("_", " ")}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack
              justifyContent="space-between"
              direction="row"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center">
                <IconButton
                  size="small"
                  onClick={() => setAccessLevel("category")}
                >
                  {accessLevel === "category" ? (
                    <RadioButtonCheckedIcon fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" />
                  )}
                </IconButton>
                <Typography fontSize={14}>Category Specific Access</Typography>
              </Stack>
              <Select
                sx={{ width: 200 }}
                size="small"
                value={specificCategory}
                disabled={accessLevel !== "category"}
                onChange={(e) => setSpecificCategory(e.target.value)}
              >
                {categoryOptions.map((item, index) => (
                  <MenuItem key={index} value={item}>
                    {item.replaceAll("_", " ")}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            {canWriteState && (
              <>
                <Stack direction="row" alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => setAccessLevel("mdm")}
                  >
                    {accessLevel === "mdm" ? (
                      <RadioButtonCheckedIcon fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Typography fontSize={14}>MDM Edit Access</Typography>
                </Stack>
                <Stack direction="row" alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => setAccessLevel("role")}
                  >
                    {accessLevel === "role" ? (
                      <RadioButtonCheckedIcon fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Typography fontSize={14}>Role Management Access</Typography>
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              editRole ? updateRoleApi() : addUserApi();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(deleteRole)} onClose={() => setDeleteRole(null)}>
        <DialogTitle>Remove User</DialogTitle>
        <DialogContent>
          <Typography>
            Do you really want to remove{" "}
            <b style={{ color: "#444" }}>{deleteRole}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRole(null)}>Cancel</Button>
          <Button variant="contained" onClick={deleteRoleApi}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}


export default RoleManagement;
